<script setup>
import { ref, computed, inject, onMounted } from 'vue'
import { getBaseUrl } from '@/helpers/fetch-wrapper'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth.store'
const { t } = useI18n()
const audiomanager = inject('audiomanager')
const { user } = useAuthStore()

const props = defineProps({
  gameState: {
    type: Object,
    required: true
  },
  // disables the "i can't listen now" button
  allowSkip: {
    type: Boolean,
    default: false
  },
  // currently just for use in the registration game, we make the buttons smaller like mobile
  compact: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['game-action'])

// Local UI state
const hasAnswered = ref(false)
const selectedAnswer = ref(null)
const revealText = ref(false)
const showControls = ref(false)

// Computed properties
const currentConcept = computed(() => props.gameState.current_concept)

const correctAnswer = computed(() => {
  const concept = currentConcept.value
  if (!concept?.native) return ''
  return concept.native.text
})

const options = ref([])

const concept = currentConcept.value

const distractors = concept.native.distractors

// Get correct answer and 3 random distractors
const answer = {
  text: correctAnswer.value,
  annotation: concept.native.text
}
const randomDistractors = distractors
  .sort(() => Math.random() - 0.5)
  .slice(0, 3)
  .map(distractor => ({
    text: distractor.text,
    annotation: distractor.annotation
  }))

// Combine and shuffle
options.value = [answer, ...randomDistractors]
  .sort(() => Math.random() - 0.5)

const questionLanguage = computed(() => 'learning')
const answerLanguage = computed(() => 'native')

// Play the audio of the learning language option
const playAudio = (slow = false) => {
  const url = getBaseUrl(`/tts/?lang=${concept.learning.language}&text=${concept.learning.text}&token=${user.token}`)
  audiomanager.setPlaybackSpeed(slow ? 0.50 : 1) // Set playback speed to 0.50 for slow, 1 for normal
  audiomanager.play(url)
}

playAudio()

const buttonClass = (option) => {
  let base_classes = ''
  if (!hasAnswered.value) base_classes = 'btn-outline-primary-cs'
  else if (hasAnswered.value && option.text === correctAnswer.value) base_classes = 'btn-success'
  else if (hasAnswered.value && option.text === selectedAnswer.value && option.text !== correctAnswer.value) base_classes = 'btn-danger'
  else if (hasAnswered.value) {
    base_classes = 'd-none'
  }
  else {
    base_classes = 'btn-outline-primary-cs'
  }
  if (props.compact) {
    return `${base_classes} btn-xs compact-btn-xs`
  }
  else {
    return `${base_classes} btn-xs`
  }
}

const hideClass = (option) => {
  if (!hasAnswered.value) return ''
  if (hasAnswered.value && option.text === correctAnswer.value) return ''
  if (hasAnswered.value && option.text === selectedAnswer.value && option.text !== correctAnswer.value) return ''
  if (hasAnswered.value) {
    return 'd-none'
  }
  return ''
}

// Methods
async function checkAnswer(selected) {
  if (hasAnswered.value) return

  selectedAnswer.value = selected.text
  hasAnswered.value = true
  revealText.value = true

  // Emit game action
  const result = await emit('game-action', {
    selected: selected.text,
    expected: correctAnswer.value,
    correct: selected.text === correctAnswer.value
  })

  // If result indicates game is complete, reset local state
  if (result?.game_state?.completed) {
    hasAnswered.value = false
    selectedAnswer.value = null
    revealText.value = false
  }
}

function disableAudio() {
  emit('disable-audio')
}
function enableActions() {
  console.log('enableClick called')
  // make this find any button with class choicebutton and enable all of them
  const buttons = document.querySelectorAll('.choicebutton')
  buttons.forEach(button => {
    button.style.pointerEvents = 'auto'
  })
}

onMounted(() => {
  console.log('add body no hover')
    setTimeout(() => {
      console.log('remove body no hover')
      enableActions()
    }, 1000); // adjust timing as needed
})

</script>

<template>
  <div class="game-container border p-3 shadow rounded-4 mt-2">
    <div class="word-to-translate mb-2 row p-4 align-items-center">
      <div class="col">
          <div class="dropmenu">
            <button
              type="button" 
              class="btn-grear"
              @click="showControls = !showControls">
              <i :class="showControls ? 'bi bi-gear' : 'bi bi-gear'"></i>
            </button>
            <div v-if="showControls" class="popout-menu">
              <ul class="text-start">
                <li @click="emit('chat-with-tutor')">
                  <i class="bi bi-chat-dots me-1"></i>
                  {{ t('course.chat-with-tutor') }}
                </li>
                <li @click="emit('example-sentences')">
                  <i class="bi bi-translate me-1"></i>
                  {{ t('curriculum.example-sentences') }}
                </li>
                <li v-if="props.gameState.vocab_entry_id" @click="router.push({ name: 'EditVocab', params: { id: props.gameState.vocab_entry_id } })" >
                  <i class="bi bi-pencil-square me-1"></i>
                  {{ t('curriculum.edit-vocab') }}
                </li>
                <li v-if="canUndo" class="d-flex align-items-center" @click="undoLastReview">
                  <i class="bi bi-arrow-counterclockwise me-1"></i>
                  {{ t('game.undo') }}
                </li>
                <li @click="playAudio(false)" >
                  <i class="bi bi-play-circle me-1"></i>
                  {{ t('curriculum.play-again') }}
                </li>
                <li @click="playAudio(true)" >
                  <i class="bi bi-play-circle me-1"></i>
                  {{ t('curriculum.play-slow') }}
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div class="col-12 text-start text-sm-center">
          <h2 class="mb-2" v-html="concept.learning.annotated.annotation" v-if="revealText"></h2>
          <div v-else class="audio-controls">
            <button type="button" class="btn btn-outline-primary btn-outline-primary-cs btn-xs-short ml-3 me-3" @click="playAudio()">
              <i class="command bi bi-volume-up-fill me-2"></i>
              {{ t('curriculum.play-again') }}
            </button>
            <button type="button"  class="btn btn-outline-primary btn-outline-primary-cs btn-xs-short mr-3" @click="playAudio(true)">
              <i class="command bi bi-volume-up-fill me-2"></i>
              <span>{{ t('curriculum.play-slow') }}</span>
            </button>
          </div>
        </div>
    </div>

    <div class="options-grid">
      <div class="row g-3">
        <div v-for="option in options" :key="option.text" class="col-12"
        :class="hideClass(option)">
          <button
            class="btn w-100 py-3 btn-xs-short choicebutton"
            :class="buttonClass(option)"
            @click="checkAnswer(option)"
            :disabled="hasAnswered"
            v-html="option.annotation"
          >
          </button>
        </div>
      </div>
    </div>
    <div class="mt-3" v-if="!revealText && allowSkip">
      <button type="button"  class="btn btn-outline-primary btn-outline-primary-cs" @click="disableAudio()">
        <i class="bi bi-volume-mute-fill"></i> {{ t('curriculum.i-cant-listen-now') }}
      </button>
    </div>

  </div>
</template>

<style scoped>
.game-container {
  max-width: 600px;
  margin: 0 auto;
  margin-bottom: 1rem;
}
.audio-controls, .audio-controls button{
  display:flex;
  justify-content:center;
}
.word-to-translate {
  background-color: #f8f9fa;
  padding: 0.5rem;
  border-radius: 8px;
  position:relative;
}
.dropmenu{
  position: absolute;
  right: 5px;
  top: 5px;
}
.dropmenu i{
  font-size:20px;
}
.btn-grear{
  background: transparent;
  padding: 5px 10px;
}
.options-grid button{
  color: #242f51!important;
  border-color:#242f51!important;
  font-size: 1.1rem;
  transition: all 0.2s ease;
}
.options-grid button.btn-success{
  background-color: #dcfce7;
  border-color: #198754!important;
  color: #198754!important;
  font-weight: 500;
}
.options-grid button.btn-danger{
  background-color: #f8d7da;
  font-weight: 500;
  border-color: #721c24!important;
  color: #721c24!important;
}

.options-grid button:hover:not(:disabled) {
  transform: translateY(-2px);
  color: white !important;
}

.compact-btn-xs {
  padding: 0.5rem 1rem !important;
  margin: -0.2rem;
  font-size: 0.8rem !important;
}

@media (max-width: 576px) {
  .btn-xs-short {
    padding: 0.5rem 1rem !important;
    margin: -0.2rem;
    font-size: 0.8rem !important;
  }
}
@media(max-width: 450px){
  .audio-controls button{
    flex-direction:column;
  }
  .dropmenu {
    position: absolute;
    right: 0px;
    top: -5px;
  }
}
.btn-outline-primary-cs{
  border-color:#242f51 !important;
  color:#242f51!important;
  font-size: 14px;
}
.btn-outline-primary-cs:hover{
  background-color: #242f51!important;
  color:#ffffff!important;
}
.score-display {
  font-size: 1.2rem;
  text-align: center;
}
/* Styles for the popout menu */
.popout-menu {
    position: absolute;
    z-index: 1000;
    background: white;
    border: 1px solid #ccc;
    border-radius: 6px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
    width: 250px;
    right:0;
    top:40px;
}

.popout-menu ul {
    list-style: none;
    margin: 0;
    padding: 0;
}

.popout-menu li {
  padding: 10px 20px;
  cursor: pointer;
  border-bottom: 1px solid #ccc;
  font-size:14px;
  font-weight:500;
}

.popout-menu li:hover {
    background-color: #f1f1f1;
}

.command::after {
  content: '\A'; /* Inserts a newline */
  white-space: pre; /* Ensures the newline is rendered */
}

.leftcommand {
  float: left;
  width: 90%;
}

.rightcommand {
  float: right;
  width: 10%;
}

@media (max-width: 576px) {
  .leftcommand {
    width: 75%;
  }

  .rightcommand {
    width: 25%;
  }
  .rightcommand > button {
    float:right;
  }
}
.bi {
  color: black;
}
button.choicebutton {
  pointer-events: none;
}
</style>